import React, { createContext, useContext, useLayoutEffect, useMemo, useRef, useState } from 'react';

const PanelGroupContext = createContext(null);
const PanelContext = createContext(null);

const normalizeKey = (key) => (key === undefined || key === null ? null : String(key));

// Mirrors bootstrap 3's .collapse/.collapsing/.collapse.in transition: animates the
// element's height between 0 and its content height instead of snapping display none/block.
const useCollapseTransition = (expanded) => {
  const nodeRef = useRef(null);
  const prevExpandedRef = useRef(expanded);
  const [phase, setPhase] = useState(expanded ? 'in' : 'closed');

  useLayoutEffect(() => {
    const node = nodeRef.current;
    if (!node || prevExpandedRef.current === expanded) {
      return;
    }
    prevExpandedRef.current = expanded;

    // Measure the starting height while the node still has its pre-transition class
    // (i.e. before 'collapsing' - which declares transition-property/duration in
    // bootstrap.min.css - is applied below).
    const startHeight = expanded ? 0 : node.scrollHeight;
    node.style.height = `${startHeight}px`;

    // Mutate the class list directly on the DOM node instead of only through
    // setState. React's re-render from setPhase is asynchronous, so if we relied on
    // it alone, both this height write and the end-height write below would land
    // before the 'collapsing' class is ever committed/painted, coalescing them into
    // a single instant jump with no visible transition.
    node.classList.remove('collapse', 'in');
    node.classList.add('collapsing');
    setPhase('collapsing');

    // Force a synchronous reflow so the browser commits the starting height on the
    // now transition-enabled node before we set the end height on the next line.
    // eslint-disable-next-line no-unused-expressions
    node.offsetHeight;

    node.style.height = expanded ? `${node.scrollHeight}px` : '0px';
  }, [expanded]);

  const handleTransitionEnd = (event) => {
    if (event.target !== nodeRef.current || event.propertyName !== 'height') {
      return;
    }
    nodeRef.current.style.height = null;
    setPhase(expanded ? 'in' : 'closed');
  };

  const stateClassName = phase === 'collapsing' ? 'collapsing' : phase === 'in' ? 'collapse in' : 'collapse';

  return { nodeRef, stateClassName, handleTransitionEnd };
};

export const PanelGroup = ({
  accordion = false,
  activeKey = null,
  onSelect,
  id,
  className = '',
  children
}) => {
  const contextValue = useMemo(
    () => ({
      accordion,
      activeKey: normalizeKey(activeKey),
      onSelect
    }),
    [accordion, activeKey, onSelect]
  );

  const rootClassName = ['panel-group', className].filter(Boolean).join(' ');

  return (
    <PanelGroupContext.Provider value={contextValue}>
      <div className={rootClassName} id={id}>
        {children}
      </div>
    </PanelGroupContext.Provider>
  );
};

const PanelRoot = ({ eventKey = null, defaultExpanded = false, className = '', children }) => {
  const groupContext = useContext(PanelGroupContext);
  const [localExpanded, setLocalExpanded] = useState(Boolean(defaultExpanded));

  const normalizedEventKey = normalizeKey(eventKey);

  const expanded = groupContext
    ? normalizeKey(groupContext.activeKey) === normalizedEventKey
    : localExpanded;

  const toggle = (event) => {
    if (groupContext && typeof groupContext.onSelect === 'function') {
      const nextKey = expanded ? null : normalizedEventKey;
      groupContext.onSelect(nextKey, event);
      return;
    }

    setLocalExpanded((prev) => !prev);
  };

  const contextValue = useMemo(
    () => ({
      expanded,
      toggle,
      eventKey: normalizedEventKey
    }),
    [expanded, normalizedEventKey]
  );

  const rootClassName = ['panel', className].filter(Boolean).join(' ');

  return (
    <PanelContext.Provider value={contextValue}>
      <div className={rootClassName}>{children}</div>
    </PanelContext.Provider>
  );
};

const Heading = ({ children, className = '' }) => {
  const headingClassName = ['panel-heading', className].filter(Boolean).join(' ');
  return <div className={headingClassName}>{children}</div>;
};

const Title = ({ children, className = '', toggle = false, onClick }) => {
  const panelContext = useContext(PanelContext);
  const isToggle = Boolean(toggle && panelContext);

  const handleClick = (event) => {
    if (typeof onClick === 'function') {
      onClick(event);
    }

    if (isToggle) {
      panelContext.toggle(event);
    }
  };

  const titleClassName = ['panel-title', className].filter(Boolean).join(' ');

  return (
    <div
      className={titleClassName}
      onClick={handleClick}
      role={isToggle ? 'button' : undefined}
      tabIndex={isToggle ? 0 : undefined}
      onKeyDown={
        isToggle
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleClick(event);
              }
            }
          : undefined
      }
    >
      {children}
    </div>
  );
};

const Body = ({ children, className = '', collapsible = false }) => {
  const panelContext = useContext(PanelContext);
  const expanded = panelContext ? panelContext.expanded : true;
  const { nodeRef, stateClassName, handleTransitionEnd } = useCollapseTransition(expanded);

  if (!collapsible) {
    const bodyClassName = ['panel-body', className].filter(Boolean).join(' ');
    return <div className={bodyClassName}>{children}</div>;
  }

  const collapseClassName = ['panel-collapse', stateClassName, className].filter(Boolean).join(' ');

  return (
    <div
      ref={nodeRef}
      className={collapseClassName}
      aria-hidden={!expanded}
      onTransitionEnd={handleTransitionEnd}
    >
      <div className='panel-body'>{children}</div>
    </div>
  );
};

const Collapse = ({ children, className = '' }) => {
  const panelContext = useContext(PanelContext);
  const expanded = panelContext ? panelContext.expanded : true;
  const { nodeRef, stateClassName, handleTransitionEnd } = useCollapseTransition(expanded);

  const collapseClassName = ['panel-collapse', stateClassName, className].filter(Boolean).join(' ');

  return (
    <div
      ref={nodeRef}
      className={collapseClassName}
      aria-hidden={!expanded}
      onTransitionEnd={handleTransitionEnd}
    >
      {children}
    </div>
  );
};

const Footer = ({ children, className = '' }) => {
  const footerClassName = ['panel-footer', className].filter(Boolean).join(' ');
  return <div className={footerClassName}>{children}</div>;
};

export const Panel = Object.assign(PanelRoot, {
  Heading,
  Title,
  Body,
  Collapse,
  Footer
});
